import { AxiosError } from 'axios';
import type { AppProps } from 'next/app';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Hydrate } from 'react-query/hydration';
import { toast } from 'react-toastify';

interface QueryProviderProps {
  pageProps: AppProps['pageProps'];
}

export default function QueryProvider({
  pageProps,
  children,
}: React.PropsWithChildren<QueryProviderProps>) {
  const { t } = useTranslation();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // **E-commerce specific staleTime:**
            // Products, categories, and other relatively static data can have a longer staleTime.
            // This reduces unnecessary network requests, improving performance.
            // For highly dynamic data (e.g., user's cart, order status), you might set a lower staleTime or 0.
            staleTime: 1000 * 60 * 5,

            // **Refetch on window focus:**
            // Generally good for e-commerce to ensure data is fresh if the user switches tabs.
            // For specific components where this might be disruptive (e.g., a form the user is filling),
            // you can disable it per-query.
            refetchOnWindowFocus: true,

            // **Retry policy for e-commerce:**
            // - Don't retry on 404 (Not Found) - usually means the resource doesn't exist.
            // - Consider not retrying on 401 (Unauthorized) or 403 (Forbidden) if it's a security-related error.
            // - For other network errors or server-side issues (5xx), retrying is generally good.
            retry: (failureCount, error) => {
              if (error instanceof AxiosError) {
                if (
                  error.response?.status === 400 ||
                  error.response?.status === 401 ||
                  error.response?.status === 403 ||
                  error.response?.status === 404
                ) {
                  return false;
                }
              }

              return failureCount < 2;
            },

            retryDelay: (attemptIndex) => {
              return Math.min(1000 * 2 ** attemptIndex, 30000);
            },

            // **Global error handling (optional):**
            // You can use the onError callback to show a global toast/notification or log errors.
            // This is good for background refetch failures where you don't want to block the UI.
            onError: (error) => {
              if (error instanceof AxiosError) {
                if (error.response?.status && error.response.status >= 500) {
                  toast.error(
                    `${t(error.response.data?.message)}` ||
                      'Something went wrong on our end. Please try again later.',
                  );
                } else if (
                  error.response?.status &&
                  error.response.status >= 400
                ) {
                  // console.warn('Client-side error during data fetch:', t(error.response.data?.message));
                  // For 4xx errors (excluding 404 handled in retry), you might show a more specific message
                  // toast.warn(t(error.response.data?.message) || 'An issue occurred.');
                } else {
                  // console.error('Network or unknown error during data fetch:', t(error.response.data?.message));
                  // toast.error("Network error. Please check your internet connection.");
                }
              } else {
                // console.error('An unexpected error occurred:', t(error.response.data?.message));
                // toast.error("An unexpected error occurred.");
              }
            },
          },
          mutations: {
            // **Mutations for e-commerce:**
            // For actions like "Add to Cart", "Checkout", "Update Profile", mutations are key.
            // By default, mutations don't retry, as retrying a "create order" multiple times could be problematic.
            retry: 0,
            onError: (error, variables, context) => {
              if (error instanceof AxiosError) {
                console.error(
                  'Mutation failed:',
                  error.config?.url,
                  error.response?.data,
                );
                // For mutations, it's often more critical to show specific user feedback.
                // toast.error(t(error?.response?.data?.message) || "Could not complete action.");
              }
            },
            // On success of a mutation, it's often good practice to invalidate related queries
            // to ensure the UI reflects the latest server state.
            // For example, after adding an item to the cart, you'd invalidate the 'cart' query.
            // onSuccess: (data, variables, context) => {
            //   queryClient.invalidateQueries(['cart']);
            // },
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>{children}</Hydrate>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
